import React, { useState, useEffect, useCallback } from 'react';
import { API_URLS } from '../config/api';
import { apiGet, apiPut, showToast } from '../utils/apiUtils';
import './AdminReportsPage.css';

const PAGE_SIZE = 20;

const FILTER_OPTIONS = [
  { value: false, label: 'Без ответа' },
  { value: true,  label: 'Отвечены' },
  { value: undefined, label: 'Все' },
];

const AdminReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(false);
  const [responding, setResponding] = useState({});
  const [responseTexts, setResponseTexts] = useState({});

  const fetchReports = useCallback(async (p = 0, f = false) => {
    setLoading(true);
    try {
      const res = await apiGet(API_URLS.ADMIN_REPORTS(p * PAGE_SIZE, PAGE_SIZE, f));
      setReports(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (e) {
      showToast('Ошибка загрузки жалоб', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(0, filter); }, [fetchReports, filter]);

  const handleFilterChange = (value) => {
    setFilter(value);
    setPage(0);
  };

  const handleRespond = async (reportId) => {
    const text = (responseTexts[reportId] || '').trim();
    setResponding(prev => ({ ...prev, [reportId]: true }));
    try {
      const res = await apiPut(API_URLS.ADMIN_REPORT_RESPOND(reportId), { admin_response: text });
      setReports(prev => prev.map(r => r.id === reportId ? res.data : r));
      showToast('Ответ сохранён', 'success');
    } catch (e) {
      showToast('Ошибка сохранения ответа', 'error');
    } finally {
      setResponding(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const formatDate = (dt) => new Date(dt).toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="arp-page">
      <div className="arp-container">
        <div className="arp-header">
          <h1 className="arp-title">Жалобы пользователей</h1>
          <button className="arp-refresh" onClick={() => fetchReports(page, filter)}>Обновить</button>
        </div>
        {!loading && <p className="arp-total">Всего: {total}</p>}

        <div className="arp-filters">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={String(opt.value)}
              className={`arp-filter-btn${filter === opt.value ? ' arp-filter-btn--active' : ''}`}
              onClick={() => handleFilterChange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="arp-empty">Загрузка...</p>
        ) : reports.length === 0 ? (
          <p className="arp-empty">Жалоб нет</p>
        ) : (
          <>
            <div className="arp-list">
              {reports.map(r => (
                <div key={r.id} className={`arp-item${r.admin_response ? ' arp-item--answered' : ''}`}>
                  <div className="arp-item-meta">
                    <span className="arp-item-id">#{r.id}</span>
                    <span className="arp-item-user">{r.username || `user #${r.user_id}`}</span>
                    {r.novel_name && <span className="arp-item-novel">«{r.novel_name}»</span>}
                    {r.game_state_id && <span className="arp-item-state">state: {r.game_state_id}</span>}
                    <span className="arp-item-date">{formatDate(r.created_at)}</span>
                  </div>
                  <div className="arp-item-text">{r.text}</div>
                  {r.admin_response ? (
                    <div className="arp-item-current-response">
                      <span className="arp-response-label">Ответ:</span>
                      <span>{r.admin_response}</span>
                    </div>
                  ) : (
                    <div className="arp-response-form">
                      <textarea
                        className="arp-response-textarea"
                        placeholder="Ответ администратора..."
                        value={responseTexts[r.id] || ''}
                        onChange={e => setResponseTexts(prev => ({ ...prev, [r.id]: e.target.value }))}
                        rows={3}
                      />
                      <button
                        className="arp-response-btn"
                        onClick={() => handleRespond(r.id)}
                        disabled={responding[r.id] || !responseTexts[r.id]?.trim()}
                      >
                        {responding[r.id] ? 'Сохранение...' : 'Сохранить ответ'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="arp-pagination">
              <button className="arp-page-btn" disabled={page === 0 || loading} onClick={() => fetchReports(page - 1, filter)}>← Пред.</button>
              <span className="arp-page-info">{page + 1} / {totalPages}</span>
              <button className="arp-page-btn" disabled={(page + 1) >= totalPages || loading} onClick={() => fetchReports(page + 1, filter)}>След. →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminReportsPage;
